import { compareAnnualMaskSignedPressure } from "./annualMaskSignedPressure";

const PDFJS_VERSION = "2.16.105";
const PDFJS_SCRIPT_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
const PDFJS_WORKER_URL = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;
const TESSERACT_SCRIPT_URL =
  "https://cdn.jsdelivr.net/npm/tesseract.js@5/dist/tesseract.min.js";

const normalizeText = (text = "") =>
  String(text || "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t\r\n]+/g, " ")
    .trim();

export const loadPdfJs = () => {
  if (window.pdfjsLib) {
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
    return Promise.resolve(window.pdfjsLib);
  }

  if (window.__cpapFollowupPdfJsLoading) {
    return window.__cpapFollowupPdfJsLoading;
  }

  window.__cpapFollowupPdfJsLoading = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = PDFJS_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (!window.pdfjsLib) {
        reject(new Error("PDF parser is not available"));
        return;
      }

      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_URL;
      resolve(window.pdfjsLib);
    };
    script.onerror = () => reject(new Error("Cannot load PDF parser"));
    document.body.appendChild(script);
  });

  return window.__cpapFollowupPdfJsLoading;
};

const loadTesseractJs = () => {
  if (window.Tesseract) return Promise.resolve(window.Tesseract);

  if (window.__cpapFollowupTesseractLoading) {
    return window.__cpapFollowupTesseractLoading;
  }

  window.__cpapFollowupTesseractLoading = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = TESSERACT_SCRIPT_URL;
    script.async = true;
    script.onload = () => {
      if (!window.Tesseract) {
        reject(new Error("OCR parser is not available"));
        return;
      }
      resolve(window.Tesseract);
    };
    script.onerror = () => reject(new Error("Cannot load OCR parser"));
    document.body.appendChild(script);
  });

  return window.__cpapFollowupTesseractLoading;
};

const extractPdfPageOcrText = async (pdf) => {
  const Tesseract = await loadTesseractJs();
  const pageTexts = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement("canvas");
    const canvasContext = canvas.getContext("2d");

    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext, viewport }).promise;

    const result = await Tesseract.recognize(canvas, "eng+tha");
    pageTexts.push(result && result.data ? result.data.text || "" : "");
  }

  return pageTexts.join("\n");
};

export const extractAnnualMaskPdfText = async (file) => {
  const pdfjsLib = await loadPdfJs();
  const buffer = await file.arrayBuffer();
  const loadingTask = pdfjsLib.getDocument({ data: buffer });
  const pdf = await loadingTask.promise;
  const pageTexts = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const textContent = await page.getTextContent();
    pageTexts.push(textContent.items.map((item) => item.str || "").join(" "));
  }

  const extractedText = pageTexts.join("\n");
  if (normalizeText(extractedText).length >= 80) return extractedText;

  try {
    return await extractPdfPageOcrText(pdf);
  } catch (error) {
    console.warn("Annual Mask Purchase OCR fallback failed:", error);
    return extractedText;
  }
};

export const readAnnualMaskSignedPressure = async (file, certificateData) => {
  try {
    return compareAnnualMaskSignedPressure(
      await extractAnnualMaskPdfText(file), certificateData,
    );
  } catch (error) {
    console.warn("Unable to compare signed certificate pressure", error);
    return { status: "unreadable" };
  }
};
